import { haversineMeters } from './routing';
import type { LatLng } from './types';

/** Reject only very loose fixes (multipath / indoor). */
export const NAV_GPS_MAX_ACCURACY_M = 120;
/** Physical teleport cap used with dt. */
export const NAV_GPS_MAX_SPEED_MPS = 55;
/** Coast along the route at last speed for at most this long. */
export const NAV_GPS_COAST_MAX_MS = 25_000;
/** Start coasting when the last accepted fix is older than this. */
export const NAV_GPS_STALE_MS = 2_000;
/** Snap marker onto the polyline within this distance. */
export const NAV_GPS_ON_ROUTE_M = 50;

export type NavGpsSample = {
  atMs: number;
  /** Last accepted raw GPS (jump checks use this, not coasted display). */
  rawPos: LatLng;
  accuracyM: number;
  speedMps: number;
  traveledMeters: number;
};

export type NavGpsFixInput = {
  pos: LatLng;
  accuracyM: number;
  speedMps: number | null;
  nowMs: number;
};

/**
 * Trustworthy enough to move the cursor / feed the rerouter.
 * Does NOT reject off-route positions — intentional detours must reroute.
 * Rejects only poor accuracy and impossible teleports (e.g. river multipath).
 */
export function shouldAcceptNavGpsFix(
  fix: NavGpsFixInput,
  lastGood: NavGpsSample | null,
): boolean {
  const accuracy = fix.accuracyM;
  if (!Number.isFinite(accuracy) || accuracy <= 0 || accuracy > NAV_GPS_MAX_ACCURACY_M) {
    return false;
  }

  if (!lastGood) return true;

  const dtSec = Math.max(0.25, (fix.nowMs - lastGood.atMs) / 1000);
  const jumpM = haversineMeters(lastGood.rawPos, fix.pos);
  const budget =
    (Math.max(lastGood.speedMps, fix.speedMps ?? 0) + 10) * dtSec +
    accuracy +
    lastGood.accuracyM +
    40;
  const hardCap = NAV_GPS_MAX_SPEED_MPS * dtSec + 150;
  if (jumpM > 70 && jumpM > Math.min(budget, hardCap)) {
    return false;
  }

  return true;
}

/** Soft clamp only while staying on-route (avoids GPS jitter rewind). */
export function clampOnRouteTraveledMeters(
  proposed: number,
  lastTraveled: number | null,
  dtSec: number,
  accuracyM: number,
): number {
  if (lastTraveled === null) return Math.max(0, proposed);
  const maxAdvance = NAV_GPS_MAX_SPEED_MPS * Math.max(0.25, dtSec) + accuracyM + 50;
  const minTravel = lastTraveled - 40;
  const maxTravel = lastTraveled + maxAdvance;
  return Math.max(minTravel, Math.min(maxTravel, proposed));
}

export function coastAdvanceMeters(
  lastSpeedMps: number,
  fallbackSpeedMps: number,
  dtSec: number,
): number {
  const speed = lastSpeedMps > 0.8 ? lastSpeedMps : Math.max(2.5, fallbackSpeedMps);
  return Math.max(0, speed * Math.max(0, dtSec));
}
